<template>
  <toggle-box
    v-if="isDesktop"
    :text="$t('DATE_FILTER')"
    :blue="blue"
    :white="white"
    :pure-icon="pureIcon"
    :rounded-corner="roundedCorner"
    ref="box"
    @close="cancel"
  >
    <template v-if="hasControl" v-slot:control>
      <slot name="control" />
    </template>

    <template v-slot:icon>
      <font-awesome-icon class="icon" :icon="faCalendarAlt" />
    </template>

    <template v-slot:content>
      <div class="date-filter" :class="{ 'has-presets': (presets && !!presets.length) }">
        <div class="main">
          <div class="calendars">
            <calendar
              :date="fromDate || undefined"
              :range="[fromDate, toDate]"
              @dateChange="setDate"
              :displayMonth="leftMonth || undefined"
              @displayMonthChange="setLeftMonth"
            />
            <calendar
              :date="toDate || undefined"
              :range="[fromDate, toDate]"
              :displayMonth="rightMonth || undefined"
              @dateChange="setDate"
              @displayMonthChange="setRightMonth"
            />
          </div>

          <div class="bottom">
            <div class="info">
              <small>
                <span>{{ $t('TIME') }}</span>
                <br/>
                <span>{{ displayRange }}</span>
              </small>
            </div>

            <primary-button :text="$t('CANCEL')" grey rounded-corner @click="cancel" />
            <primary-button :text="$t('APPLY')" blue rounded-corner @click="apply" />
          </div>
        </div>
        <div v-if="presets && !!presets.length" class="presets">
          <h4>{{ $t('PRESETS') }}</h4>
          <ul>
            <li
              v-for="preset in presets"
              :key="preset.text"
              @click="() => setRange(preset.range, true)"
            >
              {{ $t(preset.text) }}
            </li>
          </ul>
        </div>
      </div>
    </template>
  </toggle-box>
  <div class="date-filter" v-else>
    <icon-button v-if="circleIconMobile" blue :size="35" :icon="faCalendarAlt" @click="() => dateFilterBottomSheetOpen = !dateFilterBottomSheetOpen"/>
    <toggle-button
      v-else
      :blue="blue"
      :white="white"
      :disabled="disabled"
      @click="() => dateFilterBottomSheetOpen = !dateFilterBottomSheetOpen"
      :pure-icon="pureIcon"
      :rounded-corner="roundedCorner"
    >
      <template v-slot:icon>
        <font-awesome-icon class="icon" :icon="faCalendarAlt" />
      </template >
    </toggle-button>

    <bottom-sheet v-model="dateFilterBottomSheetOpen" :openY="0.2">
      <div class="main">
        <div class="calendars">
          <center>
            <calendar
              :date="fromDate || undefined"
              :range="[fromDate, toDate]"
              @dateChange="setDate"
              :displayMonth="leftMonth || undefined"
              @displayMonthChange="setLeftMonth"
            />
          </center>
        </div>
        <div v-if="presets && !!presets.length" class="presets">
          <h4>{{ $t('PRESETS') }}</h4>
          <div class='container'>
            <button
              class="item button btn"
              v-for="preset in presets"
              :key="preset.text"
              @click="() => setRange(preset.range, true)"
              type="button"
            >
              <span>{{ $t(preset.text) }}</span>
            </button>
          </div>
        </div>
        <div class="bottom">
          <div class="info">
            <small>
              <span>{{ $t('TIME') }}</span>
              <br/>
              <span>{{ displayRange }}</span>
            </small>
          </div>

          <primary-button :text="$t('CANCEL')" grey rounded-corner @click="cancel" />
          <primary-button :text="$t('APPLY')" blue rounded-corner @click="apply" />
        </div>
      </div>
    </bottom-sheet>
  </div>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';
import { createNamespacedHelpers } from 'vuex-composition-helpers';

import ToggleBox from '@/components/ToggleBox';
import Calendar from '@/components/Calendar';
import PrimaryButton from '@/components/PrimaryButton';
import IconButton from '@/components/IconButton';
import BottomSheet from '@/components/BottomSheet';
import ToggleButton from '@/components/ToggleButton';

import { DATE_FORMAT, DISPLAY_DATE_FORMAT } from '@/consts';

const { useState: useAppState } = createNamespacedHelpers('app');

const now = moment();

export default {
  name: 'DateFilter',
  components: {
    ToggleBox,
    Calendar,
    FontAwesomeIcon,
    PrimaryButton,
    IconButton,
    BottomSheet,
    ToggleButton,
  },
  props: {
    from: {
      type: String,
      default: now.format(DATE_FORMAT)
    },
    to: {
      type: String,
      default: now.format(DATE_FORMAT)
    },
    presets: {
      type: Array,
      default: () => []
    },
    circleIconMobile: {
      type: Boolean,
      default: false,
    },
    pureIcon: Boolean,
    roundedCorner: Boolean,
    blue: Boolean,
    white: Boolean
  },
  setup(props, { emit, refs, slots }) {
    const set = ref(-1);
    const fromDate = ref(props.from);
    const toDate = ref(props.to);
    const leftMonth = ref(props.from);
    const rightMonth = ref(props.to);
    const dateFilterBottomSheetOpen = ref(false);

    const { isDesktop } = useAppState(['isDesktop']);

    const hasControl = computed(() => !!slots.control);

    const setFromDate = (date) => {
      if (date > toDate.value) return false;
      fromDate.value = date;
      return true;
    };

    const setToDate = (date) => {
      if (date < fromDate.value) return false;
      toDate.value = date;
      return true;
    };

    const displayRange = computed(() => `${moment(fromDate.value).format(DISPLAY_DATE_FORMAT)} - ${moment(toDate.value).format(DISPLAY_DATE_FORMAT)}`);

    const setRange = (range, magnet) => {
      if (range[1] < range[0]) return;
      [fromDate.value, toDate.value] = range;

      if (magnet) {
        [leftMonth.value, rightMonth.value] = range;
      }
    };

    const setDate = (date) => {
      set.value = (set.value + 1) % 2;

      if (set.value === 0) {
        setRange([date, date]);
      } else {
        if (!setToDate(date)) {
          const swap = fromDate.value;
          setFromDate(date);
          setToDate(swap);
        }
      }
    };

    const setLeftMonth = (val) => {
      leftMonth.value = val;
    };

    const setRightMonth = (val) => {
      rightMonth.value = val;
    };

    const apply = () => {
      emit('filter', fromDate.value, toDate.value);
      if (isDesktop.value) {
        refs.box.toggle(false);
      } else {
        dateFilterBottomSheetOpen.value = false;
      }
    };

    const cancel = () => {
      if (isDesktop.value) {
        refs.box.toggle(false);
      } else {
        dateFilterBottomSheetOpen.value = false;
      }
      setFromDate(props.from);
      setToDate(props.to);
    };

    watch(leftMonth, (l) => {
      if (l > rightMonth.value) {
        setRightMonth(l);
      }
    });

    watch(rightMonth, (r) => {
      if (r < leftMonth.value) {
        setLeftMonth(r);
      }
    });

    return {
      faCalendarAlt,
      fromDate,
      toDate,
      hasControl,
      displayRange,
      dateFilterBottomSheetOpen,
      isDesktop,
      setFromDate,
      setToDate,
      setRange,
      setDate,
      apply,
      cancel,
      leftMonth,
      rightMonth,
      setLeftMonth,
      setRightMonth
    };
  }
};
</script>

<style scoped lang="scss">
@import "~@/assets/scss/colors";

.date-filter {
  display: flex;

  .main {
    @media (min-width: 992px) {
      width: 520px;
    }
    @media (max-width: 991px) {
      overflow-y: scroll;
      height: 100%;
      padding: 20px 0;
    }

    .calendars {
      border-bottom: 1px solid $alto;
      margin-left: -15px;
      padding-left: 15px;

      .calendar {
        vertical-align: top;
        @media (max-width: 991px) {
          padding: 0px;
        }
      }
    }

    .bottom {
      text-align: right;
      padding-top: 15px;
      padding-right: 15px;

      @media (max-width: 991px) {
        padding-bottom: 15px;
      }

      .info {
        line-height: 15px;
        padding-right: 15px;
        display: inline-block;
        vertical-align: middle;
      }

      .primary-button {
        height: 26px;
        padding-left: 15px;
        padding-right: 15px;
        font-size: 14px;
        vertical-align: middle;
        margin-left: 7px;
      }
    }
  }

  .presets {
    @media (min-width: 992px) {
      width: 200px;
      margin-top: -15px;
    }
    @media (max-width: 991px) {
      padding-right: 0px;
    }
    margin-bottom: -15px;
    border-left: 1px solid $alto;
    padding: 15px;
    box-sizing: border-box;

    h4 {
      font-size: 14px;
      font-weight: bold;
    }

    .container {
      display: flex;
      flex-flow: row wrap;
      justify-content: flex-start;

      @media (max-width: 991px) {
        padding: 0px;
      }

      .item {
        height: 23px;
        border-radius: 20px;
        display: inline-block;
        box-sizing: border-box;
        padding: 0 20px;
        color: $independence;
        background-color: $babyPowder;
        font-size: 10px;
        font-family: "Source Sans Pro";
        transition: all .2s ease-in-out;
        cursor: pointer;
        position: relative;
        user-select: none;
        box-shadow: 0px 3px 6px #00000029;
        text-transform: capitalize;
        margin: 2px 2px 6px;
      }
    }

    ul {
      font-size: 14px;
      list-style: none;
      padding-left: 15px;

      li {
        transition: all .2s ease-in-out;
        cursor: pointer;

        &:hover {
          color: $geranium;
        }
      }
    }
  }
}
</style>
